/**
 * Abrevia um texto pelas primeiras letras das duas primeiras palavras.
 *
 * @param text texto para abreviar
 *
 * @category String
 *
 * @example
 * ```ts
 * import { abbreviate } from '@ids/tools';
 *
 * abbreviate('João Gomes');
 * // "JG"
 * ```
 */
export const abbreviate = (text = '') => {
    if (!text) {
        return '';
    }
    const textWords = text.split(' ');
    const initials = textWords.length > 1
        ? `${textWords[0][0]}${textWords[textWords.length - 1][0]}`
        : `${textWords[0][0]}`;
    return initials;
};
/**
 * Remove acentos, caracteres especiais e
 * padroniza o texto em caixa baixa
 *
 * @param text texto para normalização
 *
 * @category String
 *
 * @example
 * ```ts
 * import { normalizeText } from '@ids/tools';
 *
 * normalizeText('São Paulo');
 * // "sao paulo"
 * ```
 */
export const normalizeText = (text = '') => text
    .normalize('NFKD')
    .replace(/[\u0080-\uF8FF]/g, '')
    .toLocaleLowerCase();
/**
 * Transforma texto para caixa alta/caixa baixa
 *
 * @param text texto para transformação
 * @param to para caixa alta ou caixa baixa
 *
 * @category String
 *
 * @example
 * ```ts
 * import { enforceCase } from '@ids/tools';
 *
 * normalizeText('São Paulo', 'upperCase');
 * // "SÃO PAULO"
 *
 *  * normalizeText('São Paulo', 'lowerCase');
 * // "são paulo"
 * ```
 */
export const enforceCase = (text = '', to) => to === 'upperCase' ? text.toUpperCase() : to === 'lowerCase' ? text.toLowerCase() : text;
/**
 * Transforma texto em formato kebab
 *
 * @param text texto para ser transformado
 *
 * @category String
 *
 * @example
 * ```ts
 * import { toKebabCase } from '@ids/tools';
 *
 * toKebabCase('Porto Alegre RS');
 * // "porto-alegre-rs"
 * ```
 */
export const toKebabCase = (text = '') => text
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
/**
 * Retorna a primeira ocorrência de um regex em uma string por padrão
 * pode ser configurado para retornar qualquer posição.
 *
 * @param rgx o padrão de regex a ser procurado
 * @param toMatch string onde a correspondência será procurada
 * @param [index=0] posição para ser retornada
 * @returns {string} - a ocorrência encontrada ou uma string vazia  caso nenhuma
 * correspondência for encontrada
 *
 * @category String
 *
 * @example
 * ```ts
 * import { matchByIndex } from '@ids/tools';
 *
 * const letra = `
 *  Amou daquela vez como se fosse a última
 *  Beijou sua mulher como se fosse a última
 *  [...] soluçou [...]
 * `
 *
 * matchByIndex(/\b\w*ou\w*\b/, letra) // Amou
 * matchByIndex(/\b\w*ou\w*\b/, letra, 1) // Beijou
 * matchByIndex(/\b\w*ou\w*\b/, letra, -1) // soluçou
 * ```
 */
export const matchByIndex = (rgx, toMatch, index = 0) => {
    const match = toMatch.match(rgx);
    if (match) {
        if (index >= 0 && index < match.length) {
            return match[index];
        }
        else if (index < 0 && -index <= match.length) {
            return match[match.length + index];
        }
    }
    return '';
};
/**
 * Verifica se uma string contém outra string.
 *
 * @param text String a ser executada a busca.
 * @param search String a ser buscada.
 * @param options.nomalize Se deve normalizar as strings antes de executar a busca.
 * @param options.method Método de busca a ser utilizado.
 * @returns {boolean} - true se a string for encontrada, false caso contrário.
 *
 * @category String
 *
 * @example
 * ```ts
 * import { hasString } from '@ids/tools';
 *
 * hasString('João Gomes', 'joao');
 * // true
 *
 * hasString('João Gomes', 'joao', { normalize: false });
 * // false
 *
 * hasString('João Gomes', 'joao', { method: 'startsWith' });
 * // true
 * ```
 */
export const hasString = (text = '', search = '', { normalize = true, method = 'includes', } = {}) => {
    const normalizedText = (normalize ? normalizeText(text) : text).trim();
    const normalizedSearch = (normalize ? normalizeText(search) : search).trim();
    if (method === 'equals') {
        return normalizedText === normalizedSearch;
    }
    return normalizedText[method](normalizedSearch);
};
